import React, { useEffect, useState, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  createItem,
  getCategories,
  fetchSubCategories,
  updateItem,
  getUnits,
  checkItemCode,
  getSizes,
  getItemType
} from "../../../Services/Services";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { debounce } from "lodash";

const AddItemForm = ({
  setItems,
  setOpenAddModal,
  onSuccess,
  mode = "page",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const isEditMode = location.state?.mode === "edit";
  const editData = location.state?.data;
const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const [formData, setFormData] = useState(
    editData
      ? [
        {
          name: editData.name,
          item_description: editData.item_description,
          category: editData.category
            ? { id: editData.category, name: editData.category_name }
            : null,
          sub_category: editData.sub_category
            ? { id: editData.sub_category, name: editData.subcategory_name }
            : null,
          store_item_type: editData.store_item_type
            ? { id: editData.store_item_type, ItemType: editData.store_item_type_name }
            : null,
          size: editData.size
            ? { id: editData.size, size: editData.size_name }
            : null,
          item_code: editData.item_code,
          unit: editData.unit
            ? { id: editData.unit, name: editData.unit_name }
            : null,
          min_quantity: editData.min_quantity,
          current_stock: editData.current_stock,
          max_quantity: editData.max_quantity || "",
          reorder_quantity: editData.reorder_quantity || "",
          current_stock: editData.current_stock || editData.current_stock,
          status: editData.status || "active",
          price: editData.price || "",
          total_value: editData.total_value || "",
          created_by: editData.created_by,
          division: storedDivision?.division_id || "",
          id: editData.id,
        },
      ]
      : [
        {
          unit: null,
          item_code: "",
          store_item_type: null,
          size: null,
          name: "",
          category: null,
          sub_category: null,
          min_quantity: "",
          current_stock: "",
          max_quantity: "",
          reorder_quantity: "",
          current_stock: "",
          status: "active",
          price: "",
          total_value: "",
          created_by: localStorage.getItem("user_id"),
          division: storedDivision?.division_id || "",
        },
      ]
  );

  const [units, setUnits] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [itemCodeError, setItemCodeError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState({
    type: "",
    text: "",
  });

  useEffect(() => {
    fetchCategories();
    fetchUnits();
    fetchSizes();
  }, []);

  const fetchUnits = async () => {
    const data = await getUnits();
    setUnits(Array.isArray(data) ? data : data?.results || []);
  };

  const fetchCategories = async () => {
    const data = await getCategories();
    setCategories(Array.isArray(data) ? data : data?.results || []);
  };

  const fetchSizes = async (search = '') => {
    try {
      const data = await getSizes(search);
      setSizes(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

  const fetchItemTypes = async (search = '') => {
    try {
      const data = await getItemType(search);
      setItemTypes(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Error fetching item types:", error);
    }
  };

  const handleItemCodeCheck = async (index, code, id) => {
    if (!code || code.trim() === "") return;

    try {
      const res = await checkItemCode(code, id);
      if (res.exists) {
        toast.error("Item Code already exists!");
        setItemCodeError(true);
      } else {
        setItemCodeError(false);
      }
    } catch (err) {
      console.error("Error checking item code:", err);
    }
  };

  // Calculate total value when price or stock quantity changes
  const calculateTotalValue = (index, price, stockQty) => {
    const total = (parseFloat(price) || 0) * (parseFloat(stockQty) || 0);
    return total.toFixed(2);
  };

  const handleInputChange = async (index, key, value) => {
    const updatedRows = [...formData];
    let row = { ...updatedRows[index] };

    switch (key) {
      case "category":
        row = {
          ...row,
          category: value,
          sub_category: null,
        };
        // if (value) {
        //   const subcats = await fetchSubCategories(value.id, 1, "");
        //   const subs = [...subcategories];
        //   subs[index] = subcats;
        //   setSubcategories(subs);
        // }
        break;

      case "sub_category":
        row = {
          ...row,
          sub_category: value,
        };
        break;

      case "store_item_type":
        row = {
          ...row,
          store_item_type: value,
        };
        break;

      case "size":
        row = {
          ...row,
          size: value,
        };
        break;

      case "unit":
        row.unit = value;
        break;

      case "price":
        row.price = value;
        row.total_value = calculateTotalValue(index, value, row.current_stock || row.current_stock);
        break;

      case "current_stock":
        row.current_stock = value;
        row.total_value = calculateTotalValue(index, row.price, value);
        break;

      default:
        row[key] = value;
    }
    updatedRows[index] = row;
    setFormData(updatedRows);
  };

  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (type === "category") {
        if (!newValue || newValue.trim() === "") {
          const catData = await getCategories();
          const list = Array.isArray(catData.results) ? catData.results : catData;
          setCategories(list);
        } else {
          const catData = await getCategories(1, newValue);
          const list = Array.isArray(catData.results) ? catData.results : catData;
          setCategories(list);
        }
      }

      if (type === "sub_category" && row.category) {
        if (!newValue || newValue.trim() === "") {
          const subcatData = await fetchSubCategories(row.category.id);
          setSubcategories((prev) => {
            const updated = [...prev];
            updated[index] = subcatData;
            return updated;
          });
        } else {
          const subcatData = await fetchSubCategories(row.category.id, 1, newValue);
          setSubcategories((prev) => {
            const updated = [...prev];
            updated[index] = subcatData;
            return updated;
          });
        }
      }

      if (type === "store_item_type") {
        if (!newValue || newValue.trim() === "") {
          await fetchItemTypes("");
        } else {
          await fetchItemTypes(newValue);
        }
      }

      if (type === "size") {
        if (!newValue || newValue.trim() === "") {
          await fetchSizes("");
        } else {
          await fetchSizes(newValue);
        }
      }
    }, 500),
    []
  );

  const handleChange = (index, field, value) => {
    const updated = [...formData];
    if (field === "item_code") {
      const isDuplicate = updated.some(
        (item, i) => item.item_code === value && i !== index
      );

      if (isDuplicate) {
        toast.error("❌ Duplicate Item Code not allowed!");
        return;
      }
    }

    updated[index][field] = value;
    setFormData(updated);
  };

  const addItemRow = () => {
    if (formData.length >= 5) {
      toast.error("⚠️ Maximum 5 items allowed!");
      return;
    }
    setFormData((prev) => [
      ...prev,
      {
        unit: null,
        item_code: "",
        store_item_type: null,
        size: null,
        name: "",
        item_description: "",
        category: null,
        sub_category: null,
        min_quantity: "",
        current_stock: "",
        max_quantity: "",
        reorder_quantity: "",
        current_stock: "",
        status: "active",
        price: "",
        total_value: "",
        created_by: localStorage.getItem("user_id"),
        division: storedDivision?.division_id || "",
      },
    ]);
  };

  const removeItemRow = (index) => {
    const updated = [...formData];
    updated.splice(index, 1);
    setFormData(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (itemCodeError) {
      toast.error("Please fix Item Code before submitting!");
      return;
    }
    setLoading(true);
    setResponseMessage({ type: "", text: "" });

    try {
      const user_id = localStorage.getItem("user_id");
      const updatedFormData = formData.map((item) => ({
        ...item,
        category: item.category?.id,
        sub_category: item.sub_category?.id || item.sub_category?.subcat_id,
        store_item_type: item.store_item_type?.id,
        size: item.size?.id,
        unit: item.unit?.id,
        current_stock: item.current_stock || item.current_stock,
        total_value: item.total_value,
        created_by: user_id,
        division: storedDivision?.division_id || "",
      
      }));

      if (isEditMode) {
        await updateItem(editData.id, updatedFormData[0]);
        setResponseMessage({
          type: "success",
          text: "Item updated successfully!",
        });
        toast.success("Item updated successfully!");
      } else {
        await createItem(updatedFormData);
        setResponseMessage({
          type: "success",
          text: "All items created successfully!",
        });
        toast.success("Items created successfully!");
      }

      if (setItems) {
        const latestItems = await getCategories();
        setItems(latestItems);
      }
      if (onSuccess) await onSuccess();

      if (mode === "modal") {
        if (setOpenAddModal) setOpenAddModal(false);
      } else {
        navigate("/dashboard/item-list", { state: { edited: true } });
      }

      if (!isEditMode) {
        setFormData([
          {
            name: "",
            item_description: "",
            category: null,
            sub_category: null,
            store_item_type: null,
            size: null,
            unit: null,
            item_code: "",
            min_quantity: "",
            current_stock: "",
            max_quantity: "",
            reorder_quantity: "",
            current_stock: "",
            status: "active",
            price: "",
            total_value: "",
            created_by: user_id,
          },
        ]);
      }
    } catch (error) {
      const errData = error.response?.data;

      if (errData) {
        Object.keys(errData).forEach((field) => {
          const messages = errData[field];
          if (Array.isArray(messages)) {
            messages.forEach((msg) => toast.error(msg));
          } else {
            toast.error(messages);
          }
        });
      } else {
        toast.error("Error occurred.");
      }

      setResponseMessage({
        type: "error",
        text: "Validation failed. Please check the form.",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderSizeAutocomplete = (item, index) => (
    <Autocomplete
      options={sizes}
      getOptionLabel={(option) => option?.size || ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      value={item.size || null}
      onChange={(e, val, reason) => {
        if (reason === "clear" || !val) {
          handleInputChange(index, "size", null);
          return;
        }
        handleInputChange(index, "size", val);
      }}
      onInputChange={(e, newValue, reason) => {
        if (reason === "input") {
          handleItemInputChange(index, item, newValue, "size");
        }
        if (reason === "clear") {
          handleInputChange(index, "size", null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Product Type"
          InputProps={{
            ...params.InputProps,
            sx: { height: 45 },
          }}
        />
      )}
    />
  );

  // Responsive grid settings
  const getGridSize = () => {
    if (isMobile) return 12;
    if (isTablet) return 6;
    return 3; // 4 items per row on desktop
  };

  const getGridSizeForFields = () => {
    if (isMobile) return 12;
    if (isTablet) return 6;
    return 4; // 3 items per row for some fields
  };

  return (
    <Box>
      {responseMessage.text && (
        <Alert severity={responseMessage.type} sx={{ mb: 2 }}>
          {responseMessage.text}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Buttons Section */}
        {/* <Box sx={{
          display: "flex",
          mt: { xs: 3, md: 0 },
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexDirection: isMobile ? "column" : "row",
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          py: 1
        }}>
          {!editData && (
            <Button
              variant="outlined"
              onClick={addItemRow}
              sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
            >
              + Add More
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
          >
            ← Back
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading || itemCodeError}
            sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
          </Button>
        </Box> */}
<Box sx={{
  display: "flex",
  mt: { xs: 3, md: 0 },
  justifyContent: "flex-end",
  alignItems: isMobile ? "stretch" : "center", // stretch so full-width works
  gap: 2,
  mb: 2,
  flexDirection: isMobile ? "column" : "row",
  position: "sticky",
  top: 0,
  zIndex: 1,
  bgcolor: "background.paper",
  py: 1
}}>
  {isMobile ? (
    <>
      {/* Row for the first two buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {!editData && (
          <Button
            variant="outlined"
            onClick={addItemRow}
            sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
          >
            + Add More
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
        >
          ← Back
        </Button>
      </Box>
      {/* Submit button on its own row, full width */}
      <Button
        variant="contained"
        type="submit"
        disabled={loading || itemCodeError}
        sx={{ borderRadius: 0, height: 35, width: "100%", fontSize: "0.7rem", px: 2, textTransform: "none" }}
      >
        {loading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
      </Button>
    </>
  ) : (
    // Desktop layout – all buttons in one row
    <>
      {!editData && (
        <Button
          variant="outlined"
          onClick={addItemRow}
          sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
        >
          + Add More
        </Button>
      )}
      <Button
        variant="outlined"
        onClick={() => navigate(-1)}
        sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
      >
        ← Back
      </Button>
      <Button
        variant="contained"
        type="submit"
        disabled={loading || itemCodeError}
        sx={{ borderRadius: 0, height: 35, minWidth: 120, fontSize: "0.7rem", px: 2, textTransform: "none" }}
      >
        {loading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
      </Button>
    </>
  )}
</Box>
        {/* Form Fields */}
        <Box sx={{ overflowX: "auto" }}>
          {formData.map((item, index) => (
            <Box
              key={index}
              sx={{
                mb: 3,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                bgcolor: "#fafafa"
              }}
            >
              <Grid container spacing={2}>
                {/* Category */}
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={Array.isArray(categories) ? categories : []}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={item.category || null}
                    onChange={async (e, val, reason) => {
                      if (reason === "clear" || !val) {
                        await handleItemInputChange(index, item, "", "category");
                        handleInputChange(index, "category", null);
                        return;
                      }
                      handleInputChange(index, "category", val);
                      // handleChange(index, "sub_category", null);
                    }}
                    onInputChange={(e, newValue, reason) => {
                      if (reason === "input") {
                        handleItemInputChange(index, item, newValue, "category");
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Category" required InputProps={{ ...params.InputProps, sx: { height: 45 } }} />
                    )}
                  />
                </Grid>

                {/* SubCategory */}
                {/* <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={subcategories[index] || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={item.sub_category || null}
                    onChange={async (e, val, reason) => {
                      if (reason === "clear" || !val) {
                        await handleItemInputChange(index, item, "", "sub_category");
                        handleInputChange(index, "sub_category", null);
                        return;
                      }
                      handleInputChange(index, "sub_category", val);
                    }}
                    onInputChange={(e, newValue, reason) => {
                      if (reason === "input") {
                        handleItemInputChange(index, item, newValue, "sub_category");
                      }
                      if (reason === "clear") {
                        handleInputChange(index, "sub_category", null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="SubCategory" required InputProps={{ ...params.InputProps, sx: { height: 45 } }} />
                    )}
                  />
                </Grid> */}

                {/* Size */}
                <Grid item xs={12} sm={6} md={3}>
                  {renderSizeAutocomplete(item, index)}
                </Grid>

                {/* Unit */}
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={units}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={item.unit || null}
                    onChange={(e, val) => handleInputChange(index, "unit", val)}
                    renderInput={(params) => (
                      <TextField {...params} label="UOM" required InputProps={{ ...params.InputProps, sx: { height: 45 } }} />
                    )}
                  />
                </Grid>

                {/* Item Code */}
                {/* <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Item Code"
                    value={item.item_code}
                    onBlur={() => handleItemCodeCheck(index, item.item_code, item.id)}
                    onChange={(e) => handleChange(index, "item_code", e.target.value)}
                    required
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid> */}

                {/* Item Name */}


                {/* Item Description */}
                {/* <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Item Description"
                    value={item.item_description}
                    onChange={(e) => handleChange(index, "item_description", e.target.value)}
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid> */}

                {/* Status */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={item.status}
                      label="Status"
                      onChange={(e) => handleChange(index, "status", e.target.value)}
                      sx={{ height: 45 }}
                    >
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Normal">Normal</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <TextField
                    fullWidth
                    label="Item Name"
                    value={item.name}
                    onChange={(e) => handleChange(index, "name", e.target.value)}
                    required
                    multiline
                    
                    InputProps={{ sx: { minHeight: 45 } }}
                  />
                </Grid>
                {/* Stock Quantity */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Stock Quantity"
                    type="number"
                    value={item.current_stock || item.current_stock}
                    onChange={(e) => handleInputChange(index, "current_stock", e.target.value)}
                    required
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid>

                {/* Min Quantity (Reorder Level) */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Min Quantity (Reorder Level)"
                    type="number"
                    value={item.min_quantity}
                    onChange={(e) => handleChange(index, "min_quantity", e.target.value)}
                    required
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid>

                {/* Max Quantity */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Max Quantity"
                    type="number"
                    value={item.max_quantity}
                    onChange={(e) => handleChange(index, "max_quantity", e.target.value)}
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid>

                {/* Reorder Quantity */}
                {/* <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Reorder Quantity"
                    type="number"
                    value={item.reorder_quantity}
                    onChange={(e) => handleChange(index, "reorder_quantity", e.target.value)}
                    InputProps={{ sx: { height: 45 } }}
                  />
                </Grid> */}

                {/* Price */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Price"
                    type="number"
                    value={item.price}
                    onChange={(e) => handleInputChange(index, "price", e.target.value)}
                    InputProps={{ sx: { height: 45 }, startAdornment: "₹" }}
                  />
                </Grid>

                {/* Total Value */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Total Value"
                    type="text"
                    value={item.total_value}
                    disabled
                    InputProps={{
                      sx: { height: 45, bgcolor: "#f5f5f5" },
                      startAdornment: "₹"
                    }}
                  />
                </Grid>

                {/* Remove Button */}
                {!editData && formData.length > 1 && (
                  <Grid item xs={12}>
                    <Button
                      color="error"
                      onClick={() => removeItemRow(index)}
                      variant="outlined"
                      fullWidth={isMobile}
                      sx={{ mt: 1 }}
                    >
                      Remove Item {index + 1}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
        </Box>
        <ToastContainer />
      </form>
    </Box>
  );
};

export default AddItemForm;